"use client";

import { useTranslations } from "@zapier/i18n";
import { HydratedCanvas, useCanvasActionPermissions } from "@zapier/client";
import { ShareModal, useEmitShareInteractionEvent } from "@zapier/shared";
import { useEffect } from "react";
import { CanvasPublicLink } from "./CanvasPublicLink";

interface CanvasShareModalProps {
  canvas: HydratedCanvas | undefined;
  isOpen: boolean;
  onCloseModal: () => void;
}

export function CanvasShareModal({
  canvas,
  isOpen,
  onCloseModal,
}: CanvasShareModalProps) {
  const t = useTranslations("fallbacks");
  const { emit: emitShareEvent } = useEmitShareInteractionEvent();

  const { canShare, enablePermissionsCheck } = useCanvasActionPermissions(
    canvas?.id ?? "",
  );

  useEffect(() => {
    if (isOpen) {
      enablePermissionsCheck();
    }
  }, [isOpen, enablePermissionsCheck]);

  return (
    <ShareModal
      allowSharingWithEveryone={true}
      allowSharingWithTeams={true}
      allowSharingWithAdmins={true}
      assetId={canvas?.id ?? ""}
      assetName={canvas?.name || t("untitled-asset", { assetType: "Canvas" })}
      assetOwner={canvas?.owner}
      assetType="canvas"
      // Setting to false until we're ready to add owner changing
      canChangeOwner={false}
      canShare={canShare}
      emit={(subjectId: string) => {
        emitShareEvent({
          assetId: canvas?.id,
          optionSelected: "share_canvas",
          subjectId,
        });
      }}
      footerContent={
        canvas && <CanvasPublicLink canShare={canShare} canvas={canvas} />
      }
      isOpen={isOpen}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onCloseModal();
        }
      }}
      roles={["editor", "viewer"]}
    />
  );
}
